/**
 * API Service
 *
 * Client for communicating with the backend
 */

import { getAccessToken } from '../lib/supabase';

// Use VITE_API_URL for production, fallback to api.aigentswork.com or /api for local dev
const API_BASE =
  import.meta.env.VITE_API_URL || (import.meta.env.PROD ? 'https://api.aigentswork.com' : '/api');

/**
 * Make an authenticated API request
 */
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers: HeadersInit = {
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  return fetch(url, { ...options, headers });
}

export interface ExpressionVersion {
  expressionId: string;
  version: number;
  expression: {
    version: string;
    agentId: string;
    hostname: string;
    endpoints: {
      subscribe: string;
      push: string;
      post: string;
      get: string;
    };
    root: unknown;
  };
  slug?: string;
  createdAt: number;
  changelog?: string;
}

export type VisibilityLevel = 'private' | 'group' | 'public';

export interface ExpressionListItem {
  expressionId: string;
  latestVersion: number;
  versionCount?: number;
  createdAt?: number;
  updatedAt: number;
  name?: string; // Human-readable name (agentId from expression)
  slug?: string; // SEO-friendly URL slug
  description?: string; // Short description for list views
  tags?: string[]; // Tags for filtering
  category?: string; // Category for classification
  keywords?: string[]; // Keywords for search/SEO
  visibility?: VisibilityLevel; // Visibility level (private, group, public)
  groupId?: string; // Group ID if visibility is 'group'
}

export interface PaginatedExpressionsParams {
  limit?: number;
  offset?: number;
  tags?: string[];
  category?: string;
  keywords?: string[];
  search?: string;
}

export interface PaginatedExpressionsResult {
  expressions: ExpressionListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface TagCount {
  tag: string;
  count: number;
}

export interface CategoryCount {
  category: string;
  count: number;
}

export async function fetchExpressions(): Promise<ExpressionListItem[]> {
  // Use authenticated endpoint to get only user's expressions
  const response = await authFetch(`${API_BASE}/api/expressions/my`);
  if (!response.ok) {
    throw new Error(`Failed to fetch expressions: ${response.statusText}`);
  }
  const data = await response.json();
  return data.expressions || data;
}

export async function fetchExpressionsPaginated(
  params: PaginatedExpressionsParams = {},
): Promise<PaginatedExpressionsResult> {
  const searchParams = new URLSearchParams();

  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.tags && params.tags.length > 0) {
    searchParams.set('tags', params.tags.join(','));
  }
  if (params.category) searchParams.set('category', params.category);
  if (params.keywords && params.keywords.length > 0) {
    searchParams.set('keywords', params.keywords.join(','));
  }
  if (params.search) searchParams.set('search', params.search);

  // Use authenticated endpoint to get only user's expressions
  const url = `${API_BASE}/api/expressions/my?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch expressions: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchExpressionTags(): Promise<TagCount[]> {
  const response = await fetch(`${API_BASE}/expressions/tags`);
  if (!response.ok) {
    throw new Error(`Failed to fetch tags: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchExpressionCategories(): Promise<CategoryCount[]> {
  const response = await fetch(`${API_BASE}/expressions/categories`);
  if (!response.ok) {
    throw new Error(`Failed to fetch categories: ${response.statusText}`);
  }
  return response.json();
}

export async function updateExpressionTags(
  expressionId: string,
  updates: { tags?: string[]; category?: string; keywords?: string[] },
): Promise<{ success: boolean; expressionId: string }> {
  const response = await authFetch(`${API_BASE}/expressions/${expressionId}/tags`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update expression tags: ${response.statusText}`);
  }
  return response.json();
}

export async function updateExpressionVisibility(
  expressionId: string,
  visibility: VisibilityLevel,
  groupId?: string,
): Promise<{ success: boolean; visibility: VisibilityLevel; groupId?: string }> {
  const response = await authFetch(`${API_BASE}/api/expressions/${expressionId}/visibility`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visibility, groupId }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update expression visibility: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchExpression(expressionId: string): Promise<ExpressionVersion> {
  const response = await authFetch(`${API_BASE}/expressions/${expressionId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch expression: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchExpressionVersions(expressionId: string): Promise<ExpressionVersion[]> {
  const response = await authFetch(`${API_BASE}/expressions/${expressionId}/versions`);
  if (!response.ok) {
    throw new Error(`Failed to fetch expression versions: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchExpressionVersion(
  expressionId: string,
  version: number,
): Promise<ExpressionVersion> {
  const response = await authFetch(`${API_BASE}/expressions/${expressionId}/versions/${version}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch expression version: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchHealth(): Promise<{
  status: string;
  connections: number;
  agents: number;
}> {
  const response = await fetch(`${API_BASE}/health`);
  if (!response.ok) {
    throw new Error(`Failed to fetch health: ${response.statusText}`);
  }
  return response.json();
}

export async function hideExpressions(expressionIds: string[]): Promise<{ hidden: number }> {
  const response = await fetch(`${API_BASE}/expressions/hide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expressionIds }),
  });
  if (!response.ok) {
    throw new Error(`Failed to hide expressions: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Agent Types and API
// ============================================================================

export interface Agent {
  id: string;
  name: string;
  slug: string;
  purpose?: string;
  description?: string;
  projectId?: string;
  identity?: {
    principles?: string[];
    personality?: string[];
  };
  artifactTypes?: string[];
  goals?: string[];
  actions?: Array<{
    name: string;
    description?: string;
    trigger?: string;
    steps?: string[];
  }>;
  routines?: Array<{
    name: string;
    schedule?: string;
    action: string;
  }>;
  adaptConditions?: Array<{
    when: string;
    then: string;
  }>;
  knowledge?: string[];
  sources?: Array<{
    type: string;
    url?: string;
    name?: string;
  }>;
  relations?: Array<{
    agentId: string;
    relation: string;
  }>;
  guidance?: {
    requirements?: string[];
    constraints?: string[];
    guardrails?: string[];
    qualityChecks?: string[];
    escalationRules?: string[];
  };
  properties?: Record<string, unknown>;
  modes?: string[];
  memory?: {
    type?: string;
    ttl?: number;
  };
  state?: Record<string, unknown>;
  requires?: {
    tools?: string[];
    skills?: string[];
  };
  /** Execution strategy for agent runtime (camelCase for write) */
  executionStrategy?: 'prompt-only' | 'agent-loop';
  /** Maximum steps for agent-loop strategy (camelCase for write) */
  maxAgentSteps?: number;
  /** Execution strategy (snake_case from DB) */
  execution_strategy?: 'prompt-only' | 'agent-loop';
  /** Maximum steps (snake_case from DB) */
  max_agent_steps?: number;
  /** Preferred LLM provider (camelCase for write) */
  preferredProvider?: 'openai' | 'anthropic' | 'google';
  /** Preferred LLM model (camelCase for write) */
  preferredModel?: string;
  /** Preferred LLM provider (snake_case from DB) */
  preferred_provider?: 'openai' | 'anthropic' | 'google';
  /** Preferred LLM model (snake_case from DB) */
  preferred_model?: string;
  ownerId: string;
  ownerType: 'user' | 'group';
  visibility: 'private' | 'group' | 'public';
  status: 'draft' | 'active' | 'paused' | 'archived';
  pricing?: {
    type: 'free' | 'one-time' | 'subscription' | 'per-use';
    amount?: number;
    currency?: string;
  };
  version: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgentListParams {
  limit?: number;
  offset?: number;
  status?: string;
  visibility?: string;
  projectId?: string;
  search?: string;
}

export interface AgentListResult {
  items: Agent[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchAgents(params: AgentListParams = {}): Promise<AgentListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.status) searchParams.set('status', params.status);
  if (params.visibility) searchParams.set('visibility', params.visibility);
  if (params.projectId) searchParams.set('projectId', params.projectId);
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/agents/definitions?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch agents: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchAgent(agentId: string): Promise<Agent> {
  const response = await authFetch(`${API_BASE}/api/agents/definitions/${agentId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch agent: ${response.statusText}`);
  }
  return response.json();
}

export async function createAgent(agent: Partial<Agent>): Promise<Agent> {
  const response = await authFetch(`${API_BASE}/api/agents/definitions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(agent),
  });
  if (!response.ok) {
    throw new Error(`Failed to create agent: ${response.statusText}`);
  }
  return response.json();
}

export async function updateAgent(agentId: string, updates: Partial<Agent>): Promise<Agent> {
  const response = await authFetch(`${API_BASE}/api/agents/definitions/${agentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update agent: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteAgent(agentId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/agents/definitions/${agentId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete agent: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// LLM Models API
// ============================================================================

export interface LLMModelPricing {
  inputPerMTok: number | null;
  outputPerMTok: number | null;
  webSearchPerCall: number;
  currency: 'USD';
}

export interface LLMModelCapabilities {
  webSearch: boolean;
  thinking: boolean;
  vision: boolean;
  functionCalling: boolean;
}

export interface LLMModel {
  id: string;
  displayName: string;
  description: string;
  pricing: LLMModelPricing;
  capabilities: LLMModelCapabilities;
  constraints: {
    maxContextTokens: number;
    defaultMaxOutputTokens: number;
    temperatureRange: [number, number];
  };
  status: 'ga' | 'preview' | 'deprecated';
}

export interface LLMProvider {
  id: 'anthropic' | 'google' | 'openai';
  name: string;
  vaultKey: string;
  getKeyUrl: string;
  description: string;
  models: LLMModel[];
}

export interface ModelsResponse {
  providers: LLMProvider[];
}

export interface AvailableModelsResponse {
  providers: Array<{
    id: string;
    name: string;
    models: LLMModel[];
  }>;
  missingProviders: Array<{
    id: string;
    name: string;
    vaultKey: string;
    getKeyUrl: string;
    description: string;
  }>;
}

/**
 * Fetch all supported LLM providers and models
 */
export async function fetchModels(): Promise<ModelsResponse> {
  const response = await fetch(`${API_BASE}/api/models`);
  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch models available to the current user (based on Vault API keys)
 */
export async function fetchAvailableModels(): Promise<AvailableModelsResponse> {
  const response = await authFetch(`${API_BASE}/api/models/available`);
  if (!response.ok) {
    throw new Error(`Failed to fetch available models: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Admin: Models Config API
// ============================================================================

export interface AdminModelsConfig {
  providers: Array<{
    id: string;
    name: string;
    vaultKey: string;
    getKeyUrl: string;
    description: string;
    models: Array<{
      id: string;
      displayName: string;
      description: string;
      status: 'ga' | 'preview' | 'deprecated';
    }>;
  }>;
  source: 'static' | 'database';
  updatedAt: string | null;
}

/**
 * Fetch models config for admin editing
 */
export async function fetchAdminModelsConfig(): Promise<AdminModelsConfig> {
  const response = await authFetch(`${API_BASE}/api/admin/models`);
  if (!response.ok) {
    throw new Error(`Failed to fetch models config: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update models config
 */
export async function updateAdminModelsConfig(
  providers: AdminModelsConfig['providers'],
): Promise<{ success: boolean; message: string }> {
  const response = await authFetch(`${API_BASE}/api/admin/models`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ providers }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to update models config`);
  }
  return response.json();
}

/**
 * Reset models config to static defaults
 */
export async function resetAdminModelsConfig(): Promise<{ success: boolean; message: string }> {
  const response = await authFetch(`${API_BASE}/api/admin/models/reset`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to reset models config: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Project Types and API
// ============================================================================

export interface ExpectedArtifact {
  type: 'expression' | 'report' | 'document' | 'dataset' | 'software' | 'file' | 'dashboard' | 'api';
  description?: string;
}

export interface InputSchemaField {
  type: 'string' | 'number' | 'boolean' | 'enum';
  required?: boolean;
  options?: string[]; // For enum type
  description?: string;
  default?: unknown;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  goal: string;
  description?: string;
  ownerId: string;
  ownerType: 'user' | 'group';
  groupId?: string;
  visibility: 'private' | 'group' | 'public';
  status: 'draft' | 'active' | 'paused' | 'archived';
  vision?: {
    mission?: string;
    problem?: string;
    audience?: Record<string, unknown>;
    success?: Record<string, unknown>;
  };
  phases?: Array<{
    name: string;
    description?: string;
    status?: string;
  }>;
  pricing?: {
    type: 'free' | 'one-time' | 'subscription' | 'per-use';
    amount?: number;
    currency?: string;
  };
  runMode: 'buy' | 'run' | 'both';
  version: string;
  // LLM Configuration
  /** Default LLM provider for agents in this project (camelCase for write) */
  defaultProvider?: 'openai' | 'anthropic' | 'google';
  /** Default LLM model for agents in this project (camelCase for write) */
  defaultModel?: string;
  /** Default LLM provider (snake_case from DB) */
  default_provider?: 'openai' | 'anthropic' | 'google';
  /** Default LLM model (snake_case from DB) */
  default_model?: string;
  // New execution fields
  expectedArtifacts?: ExpectedArtifact[];
  inputSchema?: Record<string, InputSchemaField>;
  guardrails?: string[];
  guidance?: Record<string, unknown>;
  // Relationships
  agentIds?: string[];
  skillIds?: string[];
  isOwner?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListParams {
  limit?: number;
  offset?: number;
  status?: string;
  visibility?: string;
  search?: string;
}

export interface ProjectListResult {
  items: Project[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchProjects(params: ProjectListParams = {}): Promise<ProjectListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.status) searchParams.set('status', params.status);
  if (params.visibility) searchParams.set('visibility', params.visibility);
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/projects?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchProject(projectId: string, retries = 3): Promise<Project> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    const response = await authFetch(`${API_BASE}/api/projects/${projectId}`);

    if (response.ok) {
      return response.json();
    }

    // For 404 errors (not found), retry with exponential backoff
    // This handles race conditions after project creation
    if (response.status === 404 && attempt < retries - 1) {
      const delay = Math.min(100 * Math.pow(2, attempt), 1000); // 100ms, 200ms, 400ms...
      await new Promise(resolve => setTimeout(resolve, delay));
      continue;
    }

    lastError = new Error(`Failed to fetch project: ${response.statusText}`);
  }

  throw lastError || new Error('Failed to fetch project after retries');
}

export async function createProject(project: Partial<Project>): Promise<Project> {
  const response = await authFetch(`${API_BASE}/api/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(project),
  });
  if (!response.ok) {
    throw new Error(`Failed to create project: ${response.statusText}`);
  }
  return response.json();
}

export async function updateProject(
  projectId: string,
  updates: Partial<Project>,
): Promise<Project> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update project: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteProject(projectId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete project: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Project Execution
// ============================================================================

export interface ProjectExecutionParams {
  input: string;
  context?: Record<string, unknown>;
  provider?: string;
  model?: string;
  maxAgentSteps?: number;
}

export interface ProjectExecutionResult {
  success: boolean;
  executionId: string;
  projectId: string;
  status: 'completed' | 'failed';
  output?: unknown;
  stepResults?: Record<string, unknown>;
  metadata?: {
    llmCalls?: number;
    toolCalls?: Array<{ toolName: string; success: boolean }>;
    reasoning?: unknown;
  };
  error?: string;
}

/**
 * Execute a project with given input and context
 */
export async function executeProject(
  projectId: string,
  params: ProjectExecutionParams,
): Promise<ProjectExecutionResult> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}/execute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to execute project: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Skill Types and API
// ============================================================================

export interface Skill {
  id: string;
  name: string;
  slug: string;
  description?: string;
  version: string;
  provides: {
    knowledge?: string[];
    actions?: Array<{
      name: string;
      description?: string;
    }>;
    types?: Array<{
      name: string;
      schema?: Record<string, unknown>;
    }>;
    situations?: Array<{
      when: string;
      then: string;
    }>;
  };
  requires?: {
    tools?: string[];
    skills?: string[];
  };
  ownerId: string;
  visibility: 'private' | 'group' | 'public';
  pricing?: {
    type: 'free' | 'one-time' | 'subscription' | 'per-use';
    amount?: number;
    currency?: string;
  };
  usageCount: number;
  rating?: number;
  reviewCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface SkillListParams {
  limit?: number;
  offset?: number;
  category?: string;
  search?: string;
}

export interface SkillListResult {
  items: Skill[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchSkills(params: SkillListParams = {}): Promise<SkillListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.category) searchParams.set('category', params.category);
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/skills?${searchParams.toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch skills: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchMySkills(params: SkillListParams = {}): Promise<SkillListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/skills/mine?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch skills: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchSkill(skillId: string): Promise<Skill> {
  const response = await fetch(`${API_BASE}/api/skills/${skillId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch skill: ${response.statusText}`);
  }
  return response.json();
}

export async function createSkill(skill: Partial<Skill>): Promise<Skill> {
  const response = await authFetch(`${API_BASE}/api/skills`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(skill),
  });
  if (!response.ok) {
    throw new Error(`Failed to create skill: ${response.statusText}`);
  }
  return response.json();
}

export async function updateSkill(skillId: string, updates: Partial<Skill>): Promise<Skill> {
  const response = await authFetch(`${API_BASE}/api/skills/${skillId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update skill: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteSkill(skillId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/skills/${skillId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete skill: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Artifact Types and API
// ============================================================================

export interface Artifact {
  id: string;
  type: 'expression' | 'report' | 'document' | 'dataset' | 'software' | 'file';
  projectId?: string;
  agentId?: string;
  title: string;
  description?: string;
  content?: unknown;
  expressionId?: string;
  visibility: 'private' | 'preview' | 'public';
  ownerId: string;
  pricing?: {
    type: 'free' | 'one-time' | 'subscription' | 'per-use';
    amount?: number;
    currency?: string;
  };
  slug: string;
  tags?: string[];
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ArtifactListParams {
  limit?: number;
  offset?: number;
  type?: string;
  visibility?: string;
  search?: string;
  projectId?: string;
}

export interface ArtifactListResult {
  items: Artifact[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchArtifacts(params: ArtifactListParams = {}): Promise<ArtifactListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.type) searchParams.set('type', params.type);
  if (params.visibility) searchParams.set('visibility', params.visibility);
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/artifacts?${searchParams.toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch artifacts: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchMyArtifacts(
  params: ArtifactListParams = {},
): Promise<ArtifactListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.type) searchParams.set('type', params.type);
  if (params.search) searchParams.set('search', params.search);
  if (params.projectId) searchParams.set('projectId', params.projectId);

  const url = `${API_BASE}/api/artifacts/mine?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch artifacts: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteArtifact(artifactId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/artifacts/${artifactId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete artifact: ${response.statusText}`);
  }
  return response.json();
}

export interface ArtifactWithFiles extends Artifact {
  isOwner: boolean;
  files: FileInfo[];
  version_num?: number;
}

export async function fetchArtifact(artifactId: string): Promise<ArtifactWithFiles> {
  const response = await authFetch(`${API_BASE}/api/artifacts/${artifactId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch artifact: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Marketplace Types and API
// ============================================================================

export interface MarketplaceItem {
  id: string;
  type: 'project' | 'agent' | 'skill' | 'artifact';
  name: string;
  description?: string;
  slug: string;
  ownerId: string;
  ownerName?: string;
  pricing?: {
    type: 'free' | 'one-time' | 'subscription' | 'per-use';
    amount?: number;
    currency?: string;
  };
  rating?: number;
  reviewCount?: number;
  usageCount?: number;
  featured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarketplaceSearchParams {
  query?: string;
  type?: string;
  category?: string;
  minRating?: number;
  priceType?: string;
  limit?: number;
  offset?: number;
}

export interface MarketplaceSearchResult {
  items: MarketplaceItem[];
  total: number;
  limit: number;
  offset: number;
}

export async function searchMarketplace(
  params: MarketplaceSearchParams = {},
): Promise<MarketplaceSearchResult> {
  const searchParams = new URLSearchParams();
  if (params.query) searchParams.set('query', params.query);
  if (params.type) searchParams.set('type', params.type);
  if (params.category) searchParams.set('category', params.category);
  if (params.minRating !== undefined) {
    searchParams.set('minRating', params.minRating.toString());
  }
  if (params.priceType) searchParams.set('priceType', params.priceType);
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }

  const url = `${API_BASE}/api/marketplace/search?${searchParams.toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to search marketplace: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchFeaturedItems(): Promise<MarketplaceItem[]> {
  const response = await fetch(`${API_BASE}/api/marketplace/featured`);
  if (!response.ok) {
    throw new Error(`Failed to fetch featured items: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

// ============================================================================
// Execution Types and API
// ============================================================================

export interface Execution {
  id: string;
  projectId?: string;
  agentId?: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  input?: unknown;
  output?: unknown;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  tokensUsed?: number;
  computeCost?: number;
  createdAt: string;
}

// ============================================================================
// Execution Event Types (Observability)
// ============================================================================

export type ExecutionEventType =
  | 'execution_started'
  | 'execution_completed'
  | 'execution_failed'
  | 'execution_cancelled'
  | 'thinking_started'
  | 'thinking_completed'
  | 'planning_started'
  | 'planning_completed'
  | 'step_started'
  | 'step_completed'
  | 'step_failed'
  | 'tool_call_started'
  | 'tool_call_completed'
  | 'tool_call_failed'
  | 'llm_call_started'
  | 'llm_call_completed'
  | 'agent_message'
  | 'progress_update';

export interface ExecutionEvent {
  id: string;
  executionId: string;
  eventType: ExecutionEventType;
  agentId?: string;
  agentName?: string;
  payload: unknown;
  timestamp: string;
  durationMs?: number;
  sequenceNumber: number;
  createdAt?: string;
}

export interface ExecutionEventListParams {
  limit?: number;
  offset?: number;
  agentId?: string;
  eventType?: string;
  afterSequence?: number;
}

export interface ExecutionEventListResult {
  items: ExecutionEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExecutionListParams {
  limit?: number;
  offset?: number;
  status?: string;
  projectId?: string;
  agentId?: string;
}

export interface ExecutionListResult {
  items: Execution[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchExecutions(
  params: ExecutionListParams = {},
): Promise<ExecutionListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.status) searchParams.set('status', params.status);
  if (params.projectId) searchParams.set('projectId', params.projectId);
  if (params.agentId) searchParams.set('agentId', params.agentId);

  const url = `${API_BASE}/api/executions?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch executions: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchExecution(executionId: string): Promise<Execution> {
  const response = await authFetch(`${API_BASE}/api/executions/${executionId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch execution: ${response.statusText}`);
  }
  return response.json();
}

export async function cancelExecution(executionId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/executions/${executionId}/cancel`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to cancel execution: ${response.statusText}`);
  }
  return response.json();
}

export async function createExecution(params: {
  projectId?: string;
  agentId?: string;
  input?: unknown;
}): Promise<Execution> {
  const response = await authFetch(`${API_BASE}/api/executions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    throw new Error(`Failed to create execution: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch execution events for observability/replay
 */
export async function fetchExecutionEvents(
  executionId: string,
  params: ExecutionEventListParams = {},
): Promise<ExecutionEventListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.agentId) searchParams.set('agentId', params.agentId);
  if (params.eventType) searchParams.set('eventType', params.eventType);
  if (params.afterSequence !== undefined) {
    searchParams.set('afterSequence', params.afterSequence.toString());
  }

  const url = `${API_BASE}/api/executions/${executionId}/events?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch execution events: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Restart a failed or cancelled execution
 */
export async function restartExecution(executionId: string): Promise<{
  success: boolean;
  originalExecutionId: string;
  newExecutionId: string;
  execution: Execution;
}> {
  const response = await authFetch(`${API_BASE}/api/executions/${executionId}/restart`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to restart execution: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Integration Types and API
// ============================================================================

export interface Integration {
  id: string;
  name: string;
  type: 'platform' | 'user' | 'marketplace';
  service: string;
  description?: string;
  capabilities: string[];
  configSchema?: unknown;
  ownerId?: string;
  pricing?: {
    type: 'free' | 'one-time' | 'subscription' | 'per-use';
    amount?: number;
    currency?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationConnection {
  id: string;
  integrationId: string;
  integration: Integration;
  connectedAt: string;
}

export async function fetchIntegrations(): Promise<Integration[]> {
  const response = await fetch(`${API_BASE}/api/integrations`);
  if (!response.ok) {
    throw new Error(`Failed to fetch integrations: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

export async function fetchMyIntegrations(): Promise<IntegrationConnection[]> {
  const response = await authFetch(`${API_BASE}/api/integrations/mine`);
  if (!response.ok) {
    throw new Error(`Failed to fetch integrations: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

export async function connectIntegration(integrationId: string): Promise<{ redirectUrl: string }> {
  const response = await authFetch(`${API_BASE}/api/integrations/${integrationId}/connect`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to connect integration: ${response.statusText}`);
  }
  return response.json();
}

export async function disconnectIntegration(integrationId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/integrations/${integrationId}/disconnect`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to disconnect integration: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Vault Types and API
// ============================================================================

export type VaultSecretType =
  | 'api_key'
  | 'oauth_token'
  | 'bearer_token'
  | 'password'
  | 'ssh_key'
  | 'certificate'
  | 'other';

export interface VaultSecret {
  id: string;
  name: string;
  provider: string;
  type: VaultSecretType;
  description?: string;
  expiresAt?: string;
  lastUsedAt?: string;
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  updatedAt: string;
}

export async function fetchVaultSecrets(): Promise<VaultSecret[]> {
  const response = await authFetch(`${API_BASE}/api/vault/secrets`);
  if (!response.ok) {
    throw new Error(`Failed to fetch vault secrets: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

export async function createVaultSecret(
  name: string,
  provider: string,
  value: string,
  type?: VaultSecretType,
  description?: string,
): Promise<VaultSecret> {
  const response = await authFetch(`${API_BASE}/api/vault/secrets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, provider, value, type, description }),
  });
  if (!response.ok) {
    throw new Error(`Failed to create vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteVaultSecret(secretId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/vault/secrets/${secretId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function revealVaultSecret(secretId: string): Promise<{ value: string }> {
  const response = await authFetch(`${API_BASE}/api/vault/secrets/${secretId}/reveal`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to reveal vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function revokeVaultSecret(secretId: string): Promise<VaultSecret> {
  const response = await authFetch(`${API_BASE}/api/vault/secrets/${secretId}/revoke`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to revoke vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function rotateVaultSecret(secretId: string, newValue: string): Promise<VaultSecret> {
  const response = await authFetch(`${API_BASE}/api/vault/secrets/${secretId}/rotate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value: newValue }),
  });
  if (!response.ok) {
    throw new Error(`Failed to rotate vault secret: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Requirement Types and API
// ============================================================================

export type RequirementPriority = 'low' | 'medium' | 'high' | 'critical';
export type RequirementStatus = 'draft' | 'active' | 'completed' | 'deprecated';

export interface AcceptanceCriteria {
  id: string;
  description: string;
  completed: boolean;
  completedAt?: string;
  notes?: string;
}

export interface Requirement {
  id: string;
  projectId: string;
  name: string;
  slug?: string;
  description?: string;
  priority: RequirementPriority;
  status: RequirementStatus;
  acceptanceCriteria: AcceptanceCriteria[];
  linkedAgents: string[];
  linkedWorkflows: string[];
  dependencies: string[];
  tags: string[];
  category?: string;
  dueDate?: string;
  estimatedEffort?: string;
  metadata?: Record<string, unknown>;
  ownerId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface RequirementListParams {
  limit?: number;
  offset?: number;
  projectId?: string;
  status?: string;
  priority?: string;
  search?: string;
}

export interface RequirementListResult {
  items: Requirement[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchRequirements(
  params: RequirementListParams = {},
): Promise<RequirementListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.projectId) searchParams.set('projectId', params.projectId);
  if (params.status) searchParams.set('status', params.status);
  if (params.priority) searchParams.set('priority', params.priority);
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/requirements?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch requirements: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchRequirement(requirementId: string): Promise<Requirement> {
  const response = await authFetch(`${API_BASE}/api/requirements/${requirementId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch requirement: ${response.statusText}`);
  }
  return response.json();
}

export async function createRequirement(requirement: Partial<Requirement>): Promise<Requirement> {
  const response = await authFetch(`${API_BASE}/api/requirements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requirement),
  });
  if (!response.ok) {
    throw new Error(`Failed to create requirement: ${response.statusText}`);
  }
  return response.json();
}

export async function updateRequirement(
  requirementId: string,
  updates: Partial<Requirement>,
): Promise<Requirement> {
  const response = await authFetch(`${API_BASE}/api/requirements/${requirementId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update requirement: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteRequirement(requirementId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/requirements/${requirementId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete requirement: ${response.statusText}`);
  }
  return response.json();
}

export interface EntityVersion {
  id: string;
  entityType: string;
  entityId: string;
  version: number;
  data: Record<string, unknown>;
  changelog?: string;
  createdBy?: string;
  createdAt: string;
}

export async function fetchRequirementVersions(requirementId: string): Promise<EntityVersion[]> {
  const response = await authFetch(`${API_BASE}/api/requirements/${requirementId}/versions`);
  if (!response.ok) {
    throw new Error(`Failed to fetch requirement versions: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

// ============================================================================
// Project Workflow Types and API
// ============================================================================

export type ProjectWorkflowStatus = 'draft' | 'active' | 'paused' | 'deprecated';
export type TriggerType = 'manual' | 'scheduled' | 'webhook' | 'event';

export interface ProjectWorkflowTrigger {
  type: TriggerType;
  config: Record<string, unknown>;
}

export interface ProjectWorkflow {
  id: string;
  projectId: string;
  name: string;
  slug?: string;
  description?: string;
  stepsDescription?: string;
  triggers: ProjectWorkflowTrigger[];
  agentsInvolved: string[];
  expectedArtifacts: string[];
  timeoutSeconds: number;
  maxRetries: number;
  parallelExecution: boolean;
  status: ProjectWorkflowStatus;
  tags: string[];
  category?: string;
  metadata?: Record<string, unknown>;
  ownerId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectWorkflowListParams {
  limit?: number;
  offset?: number;
  projectId?: string;
  status?: string;
  search?: string;
}

export interface ProjectWorkflowListResult {
  items: ProjectWorkflow[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchProjectWorkflows(
  params: ProjectWorkflowListParams = {},
): Promise<ProjectWorkflowListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.projectId) searchParams.set('projectId', params.projectId);
  if (params.status) searchParams.set('status', params.status);
  if (params.search) searchParams.set('search', params.search);

  const url = `${API_BASE}/api/workflows?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch workflows: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchProjectWorkflow(workflowId: string): Promise<ProjectWorkflow> {
  const response = await authFetch(`${API_BASE}/api/workflows/${workflowId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch workflow: ${response.statusText}`);
  }
  return response.json();
}

export async function createProjectWorkflow(
  workflow: Partial<ProjectWorkflow>,
): Promise<ProjectWorkflow> {
  const response = await authFetch(`${API_BASE}/api/workflows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(workflow),
  });
  if (!response.ok) {
    throw new Error(`Failed to create workflow: ${response.statusText}`);
  }
  return response.json();
}

export async function updateProjectWorkflow(
  workflowId: string,
  updates: Partial<ProjectWorkflow>,
): Promise<ProjectWorkflow> {
  const response = await authFetch(`${API_BASE}/api/workflows/${workflowId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update workflow: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteProjectWorkflow(workflowId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/workflows/${workflowId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete workflow: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchProjectWorkflowVersions(workflowId: string): Promise<EntityVersion[]> {
  const response = await authFetch(`${API_BASE}/api/workflows/${workflowId}/versions`);
  if (!response.ok) {
    throw new Error(`Failed to fetch workflow versions: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

// ============================================================================
// Workflow Execution API
// ============================================================================

export interface WorkflowExecutionParams {
  input: string;
  context?: Record<string, unknown>;
  enableAIPlanning?: boolean;
  enableAgentLoop?: boolean;
  maxAgentSteps?: number;
  provider?: string;
  model?: string;
}

export interface WorkflowExecutionResult {
  success: boolean;
  executionId: string;
  workflowId: string;
  status: 'completed' | 'failed';
  output?: unknown;
  plan?: unknown;
  stepResults?: Record<string, unknown>;
  error?: string;
}

export async function executeWorkflow(
  workflowId: string,
  params: WorkflowExecutionParams,
): Promise<WorkflowExecutionResult> {
  const response = await authFetch(`${API_BASE}/api/workflows/${workflowId}/execute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to execute workflow: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// User Vault Types and API (Global LLM API Keys)
// ============================================================================

export type UserVaultSecretType =
  | 'llm_api_key'
  | 'oauth_token'
  | 'api_key'
  | 'password'
  | 'bearer_token'
  | 'other';

export type LLMProvider =
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'cohere'
  | 'mistral'
  | 'groq'
  | 'together'
  | 'replicate'
  | 'fireworks'
  | 'perplexity'
  | 'other';

export type UserVaultSecretStatus = 'active' | 'revoked';

export interface UserVaultSecret {
  id: string;
  name: string;
  description?: string;
  type: UserVaultSecretType;
  provider?: LLMProvider;
  metadata?: Record<string, unknown>;
  tags: string[];
  lastUsed?: string;
  useCount: number;
  status: UserVaultSecretStatus;
  createdAt: string;
  updatedAt: string;
}

export interface UserVaultSecretListParams {
  limit?: number;
  offset?: number;
  type?: string;
  provider?: string;
  status?: string;
}

export interface UserVaultSecretListResult {
  items: UserVaultSecret[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchUserVaultSecrets(
  params: UserVaultSecretListParams = {},
): Promise<UserVaultSecretListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.type) searchParams.set('type', params.type);
  if (params.provider) searchParams.set('provider', params.provider);
  if (params.status) searchParams.set('status', params.status);

  const url = `${API_BASE}/api/user-vault?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch user vault secrets: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchUserVaultSecret(secretId: string): Promise<UserVaultSecret> {
  const response = await authFetch(`${API_BASE}/api/user-vault/${secretId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch user vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function createUserVaultSecret(params: {
  name: string;
  secret: string;
  type: UserVaultSecretType;
  provider?: LLMProvider;
  description?: string;
  metadata?: Record<string, unknown>;
  tags?: string[];
}): Promise<UserVaultSecret> {
  const response = await authFetch(`${API_BASE}/api/user-vault`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    throw new Error(`Failed to create user vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function updateUserVaultSecret(
  secretId: string,
  updates: {
    name?: string;
    description?: string;
    metadata?: Record<string, unknown>;
    tags?: string[];
  },
): Promise<UserVaultSecret> {
  const response = await authFetch(`${API_BASE}/api/user-vault/${secretId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update user vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteUserVaultSecret(secretId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/user-vault/${secretId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to delete user vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function rotateUserVaultSecret(
  secretId: string,
  newSecret: string,
): Promise<UserVaultSecret> {
  const response = await authFetch(`${API_BASE}/api/user-vault/${secretId}/rotate`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newSecret }),
  });
  if (!response.ok) {
    throw new Error(`Failed to rotate user vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function revokeUserVaultSecret(secretId: string): Promise<UserVaultSecret> {
  const response = await authFetch(`${API_BASE}/api/user-vault/${secretId}/revoke`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to revoke user vault secret: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchUserVaultSecretTypes(): Promise<
  Array<{ type: UserVaultSecretType; displayName: string }>
> {
  const response = await authFetch(`${API_BASE}/api/user-vault/types`);
  if (!response.ok) {
    throw new Error(`Failed to fetch secret types: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchLLMProviders(): Promise<
  Array<{ provider: LLMProvider; displayName: string }>
> {
  const response = await authFetch(`${API_BASE}/api/user-vault/providers`);
  if (!response.ok) {
    throw new Error(`Failed to fetch LLM providers: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Project Bookmark Types and API
// ============================================================================

export interface BookmarkedProject extends Project {
  bookmarkId: string;
  notes?: string;
  bookmarkedAt: string;
}

export interface BookmarkedProjectListResult {
  items: BookmarkedProject[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchBookmarkedProjects(
  params: { limit?: number; offset?: number } = {},
): Promise<BookmarkedProjectListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }

  const url = `${API_BASE}/api/projects/bookmarks?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch bookmarked projects: ${response.statusText}`);
  }
  return response.json();
}

export async function bookmarkProject(projectId: string, notes?: string): Promise<{ id: string }> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}/bookmark`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to bookmark project: ${response.statusText}`);
  }
  return response.json();
}

export async function removeProjectBookmark(projectId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}/bookmark`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to remove bookmark: ${response.statusText}`);
  }
  return { success: true };
}

// ============================================================================
// Project Join/Collaborate Types and API
// ============================================================================

export type CollaboratorRole = 'viewer' | 'contributor' | 'admin';
export type CollaboratorStatus = 'pending' | 'approved' | 'rejected';

export interface JoinedProject extends Project {
  collaboratorId: string;
  role: CollaboratorRole;
  collaboratorStatus: CollaboratorStatus;
  requestedAt: string;
  respondedAt?: string;
  requestMessage?: string;
}

export interface JoinedProjectListResult {
  items: JoinedProject[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchJoinedProjects(
  params: { limit?: number; offset?: number; status?: CollaboratorStatus } = {},
): Promise<JoinedProjectListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.status) {
    searchParams.set('status', params.status);
  }

  const url = `${API_BASE}/api/projects/joined?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch joined projects: ${response.statusText}`);
  }
  return response.json();
}

export async function requestToJoinProject(
  projectId: string,
  requestMessage?: string,
): Promise<{ id: string }> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ requestMessage }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to request to join: ${response.statusText}`);
  }
  return response.json();
}

export async function leaveProject(projectId: string): Promise<{ success: boolean }> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}/join`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(`Failed to leave project: ${response.statusText}`);
  }
  return { success: true };
}

export interface ProjectCollaborator {
  id: string;
  projectId: string;
  userId: string;
  role: CollaboratorRole;
  status: CollaboratorStatus;
  requestedAt: string;
  respondedAt?: string;
  respondedBy?: string;
  requestMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export async function fetchProjectCollaborators(
  projectId: string,
  status?: CollaboratorStatus,
): Promise<{ items: ProjectCollaborator[] }> {
  const searchParams = new URLSearchParams();
  if (status) {
    searchParams.set('status', status);
  }

  const url = `${API_BASE}/api/projects/${projectId}/collaborators?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch collaborators: ${response.statusText}`);
  }
  return response.json();
}

export async function respondToJoinRequest(
  projectId: string,
  collaboratorId: string,
  status: 'approved' | 'rejected',
  role?: CollaboratorRole,
): Promise<ProjectCollaborator> {
  const response = await authFetch(
    `${API_BASE}/api/projects/${projectId}/collaborators/${collaboratorId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, role }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to respond to join request: ${response.statusText}`);
  }
  return response.json();
}

export async function removeProjectCollaborator(
  projectId: string,
  collaboratorId: string,
): Promise<{ success: boolean }> {
  const response = await authFetch(
    `${API_BASE}/api/projects/${projectId}/collaborators/${collaboratorId}`,
    {
      method: 'DELETE',
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to remove collaborator: ${response.statusText}`);
  }
  return { success: true };
}

// ============================================================================
// Project Fork Types and API
// ============================================================================

export interface ForkedProject extends Project {
  forkedFromId: string;
}

export interface ForkedProjectListResult {
  items: ForkedProject[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchForkedProjects(
  params: { limit?: number; offset?: number } = {},
): Promise<ForkedProjectListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }

  const url = `${API_BASE}/api/projects/forked?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch forked projects: ${response.statusText}`);
  }
  return response.json();
}

export async function forkProject(projectId: string, name?: string): Promise<Project> {
  const response = await authFetch(`${API_BASE}/api/projects/${projectId}/fork`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fork project: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// Dashboard Metrics API
// ============================================================================

export interface DashboardMetrics {
  runningAgents: number;
  costMtd: number;
  viewsMtd: number;
  totalEarnings: number;
}

export async function fetchDashboardMetrics(): Promise<DashboardMetrics> {
  const response = await authFetch(`${API_BASE}/api/dashboard/metrics`);
  if (!response.ok) {
    // Return defaults if endpoint not available yet
    if (response.status === 404) {
      return {
        runningAgents: 0,
        costMtd: 0,
        viewsMtd: 0,
        totalEarnings: 0,
      };
    }
    throw new Error(`Failed to fetch dashboard metrics: ${response.statusText}`);
  }
  return response.json();
}

// Extended execution type with project/agent names for dashboard display
export interface ExecutionWithDetails extends Execution {
  projectName?: string;
  agentName?: string;
  tokenUsage?: {
    inputTokens?: number;
    outputTokens?: number;
    estimatedCostUsd?: number;
  };
}

export async function fetchExecutionsWithDetails(
  params: ExecutionListParams = {},
): Promise<{ items: ExecutionWithDetails[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.status) searchParams.set('status', params.status);
  searchParams.set('include', 'project,agent'); // Request related data

  const url = `${API_BASE}/api/executions?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch executions: ${response.statusText}`);
  }
  return response.json();
}

// Extended agent type with project name for dashboard display
export interface AgentWithProject extends Agent {
  projectName?: string;
  lastExecutedAt?: string;
  isRunning?: boolean;
}

export async function fetchAgentsWithProjects(
  params: AgentListParams = {},
): Promise<{ items: AgentWithProject[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  searchParams.set('include', 'project'); // Request related data

  const url = `${API_BASE}/api/agents/definitions?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch agents: ${response.statusText}`);
  }
  return response.json();
}

// Extended artifact type with project name for dashboard display
export interface ArtifactWithProject extends Artifact {
  projectName?: string;
}

export async function fetchArtifactsWithProjects(
  params: ArtifactListParams = {},
): Promise<{ items: ArtifactWithProject[]; total: number }> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  searchParams.set('include', 'project'); // Request related data

  const url = `${API_BASE}/api/artifacts/mine?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch artifacts: ${response.statusText}`);
  }
  return response.json();
}

// ============================================================================
// HITL (Human-in-the-Loop) Interaction Types and API
// ============================================================================

export type InteractionStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'timed_out';
export type InteractionPriority = 'low' | 'normal' | 'high' | 'urgent';
export type InteractionResponseMode = 'single' | 'multi';

export interface InteractionType {
  id: string;
  name: string;
  slug: string;
  description?: string;
  category?: string;
  icon?: string;
  requestSchema: Record<string, unknown>;
  responseSchema: Record<string, unknown>;
  defaultTimeout?: number;
  responseMode: InteractionResponseMode;
  allowComments: boolean;
  isBuiltin: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Interaction {
  id: string;
  executionId?: string;
  projectId: string;
  agentId?: string;
  interactionTypeId: string;
  interactionType?: InteractionType;
  title: string;
  description?: string;
  requestPayload: Record<string, unknown>;
  requestContext?: Record<string, unknown>;
  responsePayload?: Record<string, unknown>;
  status: InteractionStatus;
  priority: InteractionPriority;
  assigneeId?: string;
  assigneeGroupId?: string;
  respondedBy?: string;
  respondedAt?: string;
  resolution?: string;
  resolutionSummary?: string;
  timeoutAt?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  projectName?: string;
  agentName?: string;
}

export interface InteractionResponse {
  id: string;
  interactionId: string;
  responderId: string;
  responderType: 'user' | 'agent';
  parentId?: string;
  content?: string;
  payload?: Record<string, unknown>;
  createdAt: string;
}

export interface InteractionComment {
  id: string;
  interactionId: string;
  authorId: string;
  parentId?: string;
  content: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InteractionListParams {
  limit?: number;
  offset?: number;
  status?: string;
  priority?: string;
  projectId?: string;
  agentId?: string;
  assigneeId?: string;
  type?: string;
}

export interface InteractionListResult {
  items: Interaction[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchInteractions(
  params: InteractionListParams = {},
): Promise<InteractionListResult> {
  const searchParams = new URLSearchParams();
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }
  if (params.status) searchParams.set('status', params.status);
  if (params.priority) searchParams.set('priority', params.priority);
  if (params.projectId) searchParams.set('projectId', params.projectId);
  if (params.agentId) searchParams.set('agentId', params.agentId);
  if (params.assigneeId) searchParams.set('assigneeId', params.assigneeId);
  if (params.type) searchParams.set('type', params.type);

  const url = `${API_BASE}/api/interactions?${searchParams.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch interactions: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchInteraction(interactionId: string): Promise<Interaction> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch interaction: ${response.statusText}`);
  }
  return response.json();
}

export async function respondToInteraction(
  interactionId: string,
  payload: Record<string, unknown>,
): Promise<Interaction> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payload }),
  });
  if (!response.ok) {
    throw new Error(`Failed to respond to interaction: ${response.statusText}`);
  }
  return response.json();
}

export async function cancelInteraction(interactionId: string): Promise<Interaction> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/cancel`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to cancel interaction: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchInteractionResponses(
  interactionId: string,
): Promise<InteractionResponse[]> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/responses`);
  if (!response.ok) {
    throw new Error(`Failed to fetch interaction responses: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

export async function addInteractionResponse(
  interactionId: string,
  params: { content?: string; payload?: Record<string, unknown>; parentId?: string },
): Promise<InteractionResponse> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/responses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    throw new Error(`Failed to add response: ${response.statusText}`);
  }
  return response.json();
}

export async function resolveInteraction(
  interactionId: string,
  resolution: string,
  summary?: string,
): Promise<Interaction> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resolution, summary }),
  });
  if (!response.ok) {
    throw new Error(`Failed to resolve interaction: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchInteractionComments(
  interactionId: string,
): Promise<InteractionComment[]> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/comments`);
  if (!response.ok) {
    throw new Error(`Failed to fetch interaction comments: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

export async function addInteractionComment(
  interactionId: string,
  content: string,
  isInternal?: boolean,
): Promise<InteractionComment> {
  const response = await authFetch(`${API_BASE}/api/interactions/${interactionId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, isInternal }),
  });
  if (!response.ok) {
    throw new Error(`Failed to add comment: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchPendingInteractionsCount(): Promise<{ count: number }> {
  const response = await authFetch(`${API_BASE}/api/interactions/pending-count`);
  if (!response.ok) {
    // Return 0 if endpoint not available
    if (response.status === 404) {
      return { count: 0 };
    }
    throw new Error(`Failed to fetch pending count: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchInteractionTypes(): Promise<InteractionType[]> {
  const response = await authFetch(`${API_BASE}/api/interactions/types`);
  if (!response.ok) {
    throw new Error(`Failed to fetch interaction types: ${response.statusText}`);
  }
  const data = await response.json();
  return data.items || data;
}

// ============================================================================
// Project Interaction Requirements
// ============================================================================

export interface ProjectInteractionRequirement {
  id: string;
  projectId: string;
  interactionTypeId: string;
  triggerType: 'phase' | 'event' | 'manual' | 'always';
  triggerCondition?: Record<string, unknown>;
  titleTemplate?: string;
  descriptionTemplate?: string;
  defaultPayload?: Record<string, unknown>;
  defaultAssigneeId?: string;
  defaultAssigneeGroupId?: string;
  required: boolean;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdAt?: string;
  interactionType?: InteractionType;
}

export interface ProjectInteractionRequirementListResult {
  data: ProjectInteractionRequirement[];
  total: number;
}

export async function fetchProjectInteractionRequirements(
  projectId: string,
): Promise<ProjectInteractionRequirementListResult> {
  const response = await authFetch(
    `${API_BASE}/api/projects/${projectId}/interaction-requirements`,
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch project interaction requirements: ${response.statusText}`);
  }
  return response.json();
}

export async function createProjectInteractionRequirement(
  projectId: string,
  data: Omit<ProjectInteractionRequirement, 'id' | 'projectId' | 'createdAt'>,
): Promise<ProjectInteractionRequirement> {
  const response = await authFetch(
    `${API_BASE}/api/projects/${projectId}/interaction-requirements`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to create project interaction requirement: ${response.statusText}`);
  }
  return response.json();
}

export async function updateProjectInteractionRequirement(
  projectId: string,
  requirementId: string,
  data: Partial<ProjectInteractionRequirement>,
): Promise<ProjectInteractionRequirement> {
  const response = await authFetch(
    `${API_BASE}/api/projects/${projectId}/interaction-requirements/${requirementId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to update project interaction requirement: ${response.statusText}`);
  }
  return response.json();
}

export async function deleteProjectInteractionRequirement(
  projectId: string,
  requirementId: string,
): Promise<void> {
  const response = await authFetch(
    `${API_BASE}/api/projects/${projectId}/interaction-requirements/${requirementId}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error(`Failed to delete project interaction requirement: ${response.statusText}`);
  }
}
// ============================================================================
// File API
// ============================================================================

export type FileContext = 'project' | 'skill' | 'artifact';

export interface FileInfo {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface UploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  storagePath: string;
  expiresIn: number;
}

export interface DownloadUrlResponse {
  downloadUrl: string;
  filename: string;
  displayName?: string;
  mimeType: string;
  sizeBytes: number;
  expiresIn: number;
}

export interface AllowedTypesResponse {
  allowedMimeTypes: string[];
  maxFileSize: number;
  maxFileSizeMB: number;
}

/**
 * Get allowed file types and size limits
 */
export async function fetchAllowedFileTypes(): Promise<AllowedTypesResponse> {
  const response = await authFetch(`${API_BASE}/api/files/allowed-types`);
  if (!response.ok) {
    throw new Error(`Failed to fetch allowed types: ${response.statusText}`);
  }
  return response.json();
}

/**
 * List files for a context (project, skill, or artifact)
 */
export async function fetchFiles(
  context: FileContext,
  contextId: string,
): Promise<{ files: FileInfo[] }> {
  const searchParams = new URLSearchParams({
    context,
    contextId,
  });

  const response = await authFetch(`${API_BASE}/api/files?${searchParams.toString()}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch files: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get signed URL for uploading a file
 */
export async function getFileUploadUrl(params: {
  context: FileContext;
  contextId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  displayName?: string;
  description?: string;
}): Promise<UploadUrlResponse> {
  const response = await authFetch(`${API_BASE}/api/files/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to get upload URL: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Confirm file upload completed
 */
export async function confirmFileUpload(fileId: string): Promise<{ message: string; file: FileInfo }> {
  const response = await authFetch(`${API_BASE}/api/files/${fileId}/confirm`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to confirm upload: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get signed URL for downloading a file
 */
export async function getFileDownloadUrl(fileId: string): Promise<DownloadUrlResponse> {
  const response = await authFetch(`${API_BASE}/api/files/${fileId}/download-url`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to get download URL: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Delete a file
 */
export async function deleteFile(fileId: string): Promise<{ message: string; fileId: string }> {
  const response = await authFetch(`${API_BASE}/api/files/${fileId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to delete file: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Upload a file (complete flow: get URL, upload to GCS, confirm)
 */
export async function uploadFile(
  file: File,
  context: FileContext,
  contextId: string,
  options?: {
    displayName?: string;
    description?: string;
    onProgress?: (progress: number) => void;
  },
): Promise<FileInfo> {
  // 1. Get upload URL
  const { fileId, uploadUrl } = await getFileUploadUrl({
    context,
    contextId,
    filename: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    displayName: options?.displayName,
    description: options?.description,
  });

  // 2. Upload to GCS using signed URL
  const uploadResponse = await new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && options?.onProgress) {
        options.onProgress((e.loaded / e.total) * 100);
      }
    });

    xhr.addEventListener('load', () => {
      resolve(new Response(null, { status: xhr.status }));
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file to storage: ${uploadResponse.status}`);
  }

  // 3. Confirm upload
  const { file: uploadedFile } = await confirmFileUpload(fileId);
  return uploadedFile;
}

/**
 * Download a file (opens in new tab or triggers download)
 */
export async function downloadFile(fileId: string, openInNewTab = false): Promise<void> {
  const { downloadUrl, filename } = await getFileDownloadUrl(fileId);

  if (openInNewTab) {
    window.open(downloadUrl, '_blank');
  } else {
    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
